import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Bot, Plus } from "lucide-react";
import toast from "react-hot-toast";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { AgentActionsMenu } from "@/components/Agents/AgentActionsMenu";
import { ArchiveAgentDialog } from "@/components/Agents/ArchiveAgentDialog";
import { useAgents } from "@/components/Agents/AgentsContext";
import { agentSectionPath, agentSettingsNavItems } from "@/lib/agentNav";
import { agentsHref } from "@/lib/atwAgentsHost";
import { type AgentStatus, type OrgAgent } from "@/lib/agentsDemoData";

type AgentFilter = "all" | "active" | "inactive" | "archive";

const FILTERS: { id: AgentFilter; label: string }[] = [
  { id: "all", label: "All" },
  { id: "active", label: "Active" },
  { id: "inactive", label: "Inactive" },
  { id: "archive", label: "Archive" },
];

function statusBadge(status: AgentStatus) {
  if (status === "active") return <Badge>Active</Badge>;
  if (status === "paused") return <Badge variant="secondary">Inactive</Badge>;
  return <Badge variant="outline">Archived</Badge>;
}

function matchesFilter(agent: OrgAgent, filter: AgentFilter): boolean {
  if (filter === "all") return agent.status !== "archived";
  if (filter === "active") return agent.status === "active";
  if (filter === "inactive") return agent.status === "paused";
  return agent.status === "archived";
}

export function AgentsRosterPage() {
  const {
    org,
    may,
    viewOnly,
    archiveAgent,
    setAgentStatus,
    duplicateAgent,
  } = useAgents();
  const navigate = useNavigate();
  const [filter, setFilter] = useState<AgentFilter>("all");
  const [archiveTarget, setArchiveTarget] = useState<OrgAgent | null>(null);
  const [archiveBusy, setArchiveBusy] = useState(false);
  const [archiveError, setArchiveError] = useState<string | null>(null);

  const canManage = may("create_edit_archive_agents");
  const hasAnyAgents = org.agents.length > 0;
  const filteredAgents = useMemo(
    () => org.agents.filter((agent) => matchesFilter(agent, filter)),
    [org.agents, filter],
  );

  const goCreate = () => navigate(agentsHref("/agents/new"));

  const confirmArchive = (password: string) => {
    if (!archiveTarget) return;
    // Beta: password entry is the agreement gate. Cognito step-up can replace this later.
    if (!password.trim()) {
      setArchiveError("Enter your password to agree to archive this agent.");
      return;
    }
    setArchiveBusy(true);
    setArchiveError(null);
    archiveAgent(archiveTarget.id);
    toast.success(`${archiveTarget.name} archived. History is kept.`);
    setArchiveBusy(false);
    setArchiveTarget(null);
  };

  if (!hasAnyAgents) {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center px-4 text-center">
        <div className="flex size-14 items-center justify-center rounded-2xl bg-sky-500/10 text-sky-500">
          <Bot className="size-7" aria-hidden />
        </div>
        <h1 className="mt-5 text-2xl font-semibold text-gray-900 dark:text-white">
          Get started creating your first Workforce agent
        </h1>
        <p className="mt-2 max-w-md text-sm text-gray-600 dark:text-gray-400">
          Agents are the workers in your organization. Create one to begin assigning work from the
          shared credit pool.
        </p>
        <Button
          type="button"
          className="mt-6"
          disabled={!canManage}
          onClick={goCreate}
          title={
            viewOnly
              ? "Standard role is view only. Ask a Super Admin to add an agent."
              : "Add New Agent"
          }
        >
          <Plus className="size-4" aria-hidden />
          Add New Agent
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Agents</h1>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            Each agent is a worker. Open one for details, or use the menu for settings and status.
          </p>
        </div>
        <Button
          type="button"
          disabled={!canManage}
          onClick={goCreate}
          title={
            viewOnly
              ? "Standard role is view only. Ask a Super Admin to add an agent."
              : "Add New Agent"
          }
        >
          <Plus className="size-4" aria-hidden />
          Add New Agent
        </Button>
      </div>

      <div
        className="flex flex-wrap gap-1 rounded-lg border border-gray-200 bg-white p-1 dark:border-white/10 dark:bg-[#1D232D]"
        role="tablist"
        aria-label="Filter agents"
      >
        {FILTERS.map((item) => {
          const selected = filter === item.id;
          return (
            <button
              key={item.id}
              type="button"
              role="tab"
              aria-selected={selected}
              onClick={() => setFilter(item.id)}
              className={
                selected
                  ? "rounded-md bg-gray-900 px-3 py-1.5 text-sm font-medium text-white dark:bg-white dark:text-gray-900"
                  : "rounded-md px-3 py-1.5 text-sm font-medium text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-white/5"
              }
            >
              {item.label}
            </button>
          );
        })}
      </div>

      <ul className="divide-y divide-gray-200 rounded-lg border border-gray-200 bg-white dark:divide-white/10 dark:border-white/10 dark:bg-[#1D232D]">
        {filteredAgents.map((agent) => (
          <li
            key={agent.id}
            className="relative flex flex-wrap items-center justify-between gap-3 px-4 py-3 first:rounded-t-lg last:rounded-b-lg"
          >
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <p className="font-medium text-gray-900 dark:text-white">{agent.name}</p>
                {statusBadge(agent.status)}
              </div>
              <p className="mt-0.5 text-xs text-gray-500">
                Owner {agent.humanOwnerName} · {agent.creditsBurnedMonth.toLocaleString()} credits
                this month · Last active {new Date(agent.lastActiveAt).toLocaleString()}
              </p>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <Link
                to={agentsHref(`/agents/${agent.id}`)}
                className="inline-flex h-8 items-center justify-center rounded-md border border-gray-300 bg-white px-3 text-xs font-semibold text-gray-900 hover:bg-gray-50 dark:border-white/25 dark:bg-transparent dark:text-white dark:hover:bg-white/10"
              >
                View
              </Link>
              <AgentActionsMenu
                status={agent.status}
                disabled={!canManage}
                settingsItems={agentSettingsNavItems().map((item) => ({
                  id: item.id,
                  label: item.name,
                  icon: item.icon,
                  onSelect: () =>
                    navigate(agentsHref(agentSectionPath(agent.id, item.id))),
                }))}
                onEdit={() => navigate(agentsHref(agentSectionPath(agent.id, "dashboard")))}
                onDuplicate={() => {
                  if (!canManage) return;
                  const id = duplicateAgent(agent.id);
                  if (!id) {
                    toast.error("Could not duplicate that agent.");
                    return;
                  }
                  toast.success(`${agent.name} duplicated.`);
                  navigate(agentsHref(agentSectionPath(id, "dashboard")));
                }}
                onActivate={() => {
                  if (!canManage) return;
                  setAgentStatus(agent.id, "active");
                  toast.success(`${agent.name} is active.`);
                }}
                onDeactivate={() => {
                  if (!canManage) return;
                  setAgentStatus(agent.id, "paused");
                  toast.success(`${agent.name} is inactive.`);
                }}
                onArchive={() => {
                  if (!canManage) return;
                  setArchiveError(null);
                  setArchiveTarget(agent);
                }}
              />
            </div>
          </li>
        ))}
        {filteredAgents.length === 0 ? (
          <li className="px-4 py-10 text-center text-sm text-gray-500">
            {filter === "archive"
              ? "No archived agents."
              : filter === "inactive"
                ? "No inactive agents."
                : filter === "active"
                  ? "No active agents."
                  : "No agents in this filter."}
          </li>
        ) : null}
      </ul>

      <ArchiveAgentDialog
        open={Boolean(archiveTarget)}
        agentName={archiveTarget?.name ?? ""}
        busy={archiveBusy}
        error={archiveError}
        onCancel={() => {
          if (archiveBusy) return;
          setArchiveTarget(null);
          setArchiveError(null);
        }}
        onConfirm={confirmArchive}
      />
    </div>
  );
}
